// WorkingHourController.js
import React, { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import { DateTime } from "luxon";
import { ConvertWorkingHoursToSameDateInTimeZone } from "../../util/workingHour";
import { useSelectedBusiness } from "../../redux/store";
import {
    EntityType,
    updateBusinessWorkingHour,
    updateStaffWorkingHour,
    updateWorkingHourGroup,
    WorkingHourOperation,
} from "../../redux/actions/businessActions";
import {
    ADD_WORKING_HOUR,
    DELETE_WORKING_HOUR,
    DELETE_WORKING_HOUR_OVERRIDE_GROUP,
    UPDATE_WORKING_HOUR,
    UPDATE_WORKING_HOUR_GROUP,
} from "../../graphql/mutations";
import { GET_WORKING_HOUR_OVERRIDE_GROUPS } from "../../graphql/queries";
import { useConfirm } from "material-ui-confirm";

export const WorkingHourType = {
    BASIC: "basic",
    OVERRIDE: "override",
    HOLIDAY: "holiday",
};

export const convertTimeToMinutes = (time) => {
    const period = time.slice(-2);
    const timePart = time.slice(0, -2).trim();
    const [hours, minutes] = timePart.split(":").map(Number);
    let totalMinutes = hours * 60 + minutes;
    if (period.toLowerCase() === "pm" && hours !== 12) {
        totalMinutes += 12 * 60;
    } else if (period.toLowerCase() === "am" && hours === 12) {
        totalMinutes -= 12 * 60;
    }
    return totalMinutes;
};

export const formatTimeWithPeriod = (timeInMinutes, initialPeriod) => {
    const totalMinutesInDay = 24 * 60;
    const normalizedMinutes = timeInMinutes % totalMinutesInDay;
    const hours24 = Math.floor(normalizedMinutes / 60);
    const minutes = normalizedMinutes % 60;
    const period = hours24 >= 12 ? "pm" : "am";
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
    return `${hours12.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${period}`;
};


export const useWorkingHourController = (props) => {
    const confirm = useConfirm();
    const [timeRangeErrors, setTimeRangeErrors] = useState([]);
    const [hasErrors, setHasErrors] = useState(false);
    const [updateWorkingHourData] = useMutation(UPDATE_WORKING_HOUR);
    const [deleteWorkingHourData] = useMutation(DELETE_WORKING_HOUR);
    const [addWorkingHourData] = useMutation(ADD_WORKING_HOUR);
    const [workingHourGroup, setWorkingHourGroup] = useState([]);
    const [deleteWorkingHourGroupData] = useMutation(DELETE_WORKING_HOUR_OVERRIDE_GROUP);
    const [updateWorkingHourGroupData] = useMutation(UPDATE_WORKING_HOUR_GROUP);

    const { data: workingHourOverrideGroups, refetch: refetchOverrides } = useQuery(
        GET_WORKING_HOUR_OVERRIDE_GROUPS,
        {
            variables: {
                staffId: Number(props?.staffData?.id),
        },
        skip: props.entityType !== EntityType.STAFF,
        }
    );

    const dispatch = useDispatch();
    const selectedBusiness = useSelectedBusiness();
    const selectedBusinessId = selectedBusiness?.businessBasic.id;

    useEffect(() => {
        setHasErrors(timeRangeErrors.length > 0);
    }, [timeRangeErrors]);

    const handleUpdateWorkingHourGroup = useCallback(
        (workingHourGroup, workingHours, timezone) => {
            if (!workingHourGroup || !timezone) {
                console.warn("Missing workingHourGroup or timezone. Cannot update.");
                return;
            }

            updateWorkingHourGroupData({
                variables: {
                    input: {
                        timeZoneId: timezone.id,
                        workingHours: workingHours,
                    },
                    groupId: Number(workingHourGroup.id),
                },
            })
                .then((res) => {
                    // Extract and clean response data
                    let updateWorkingHourGroupResponse = res.data?.updateWorkingHourGroup;
                    const { __typename, ...rest } = updateWorkingHourGroupResponse;

                    if (props.entityType === EntityType.STAFF) {
                        const staff = selectedBusiness?.businessStaffs.find(
                            (staff) => Number(staff.id) === props?.providerUser?.staffId
                        );
                        dispatch(updateWorkingHourGroup(staff.id, selectedBusinessId, rest, WorkingHourOperation.UPDATE));
                    } else if (props.entityType === EntityType.BUSINESS) {
                        dispatch(updateWorkingHourGroup(null, selectedBusinessId, rest, WorkingHourOperation.UPDATE));
                    }
                })
                .catch((error) => {
                    console.error("Error updating working hour:", error);
                });
        },
        [
            updateWorkingHourGroupData,
            props.entityType,
            props?.providerUser?.staffId,
            selectedBusiness?.businessStaffs,
            dispatch,
            selectedBusinessId,
        ]
    );

    const handleTimezoneSelect = useCallback(
        (timezone) => {
            if (!timezone) {
                console.warn("No timezone selected.");
                return;
            }

            if (!workingHourGroup) {
                console.warn("No workingHourGroup available.");
                return;
            }

            // Check if the timezone has actually changed
            if (timezone.id === workingHourGroup.timeZone?.id) { 
                return;
            }
            const workingHoursArray = Object.values(workingHourGroup.workingHours); // Convert to array
            const workingHoursWithoutTypename = workingHoursArray.map(({ __typename, ...rest }) => rest);

            const workingHours = ConvertWorkingHoursToSameDateInTimeZone(
                workingHoursWithoutTypename,
                workingHourGroup.timeZone,
                timezone
            );

            handleUpdateWorkingHourGroup(workingHourGroup, workingHours, timezone); // Pass the latest workingHourGroup
        },
        [workingHourGroup, handleUpdateWorkingHourGroup]
    );

    const showOrHidRow = (parent, timeZoneId) => {
        // Create a new object to avoid mutating the original parent object
        const updatedParent = {
            ...parent,
            onJob: !parent.onJob, // Toggle the onJob property  
            id: Number(parent.id), // Ensure id is Integer
            groupId: Number(parent.groupId), // Ensure groupId is Integer      
        };

        const { __typename, ...inputWithoutTypename } = updatedParent;

        updateWorkingHourData({
            variables: { input: inputWithoutTypename, timeZoneId: timeZoneId },
        })
            .then((res) => {
                let updatedWorkingHoursResponse = res.data?.updateWorkingHour;
                if (updatedWorkingHoursResponse && typeof updatedWorkingHoursResponse === "object") {
                    const { __typename, ...rest } = updatedWorkingHoursResponse;
                    updatedWorkingHoursResponse = rest;
                }

                // Dispatch updated working hours to the store
                updateWorkingHour(selectedBusinessId, updatedWorkingHoursResponse, WorkingHourOperation.UPDATE);
            })
            .catch((error) => {
                console.error("Error updating working hour:", error);
            });
    };

    const getWorkingHourOverrides = (isCustom, defaultWH) => {
        let workingHourGroup;

        if (props.entityType === EntityType.STAFF) {
            const staff = selectedBusiness?.businessStaffs.find(
                (staff) => parseInt(staff.id) === props?.providerUser?.staffId
            );
            workingHourGroup = staff?.workingHourGroups.find(
                (group) => group.custom === isCustom && group.def === defaultWH
            );
        } else if (props.entityType === EntityType.BUSINESS) {
            workingHourGroup = selectedBusiness?.workingHourGroups.find(
                (group) => group.custom === isCustom && group.isForBusiness === 1
            );
        }

        if (workingHourGroup) {
            setWorkingHourGroup(workingHourGroup);
        } else {
            setWorkingHourGroup([]);
        }
    };

    const getWorkingHourHolidays = (isCustom, defaultWH) => {
        let workingHourGroup;

        if (props.entityType === EntityType.STAFF) {
            const staff = selectedBusiness?.businessStaffs.find(
                (staff) => parseInt(staff.id) === props?.providerUser?.staffId
            );
            workingHourGroup = staff?.workingHourGroups.find(
                (group) => group.custom === isCustom && group.def === defaultWH
            );
        } else if (props.entityType === EntityType.BUSINESS) {
            workingHourGroup = selectedBusiness?.workingHourGroups.find(
                (group) => group.custom === isCustom && group.isForBusiness === 1
            );
        }

        if (workingHourGroup) {
            setWorkingHourGroup(workingHourGroup);
        } else {
            setWorkingHourGroup([]);
        }
    };

    const getWorkingHours = () => {
        let workingHourGroup;

        if (props.entityType === EntityType.STAFF) {
            const staff = selectedBusiness?.businessStaffs.find(
                (staff) => parseInt(staff.id) === props?.providerUser?.staffId
            );
            if (props.groupId) {
                workingHourGroup = staff?.workingHourGroups.find((group) => Number(group.id) === Number(props.groupId));
            } else {
                workingHourGroup = staff?.workingHourGroups.find(
                    (group) => group.custom === props.isCustom && group.def === props.defaultWH
                );
            }
        } else if (props.entityType === EntityType.BUSINESS) {
            workingHourGroup = selectedBusiness?.workingHourGroups.find(
                (group) => group.custom === props.isCustom && group.isForBusiness === 1
            );
        }

        if (workingHourGroup) {
            setWorkingHourGroup(workingHourGroup);
        } else {
            setWorkingHourGroup([]);
        }
    };

    const getUpdatedWorkingHour = (updatedWorkingHour) => {
        if (!updatedWorkingHour) return null;

        const transformWorkingHour = (workingHour) => {
            const { __typename, ...rest } = workingHour;
            return {
                ...rest,
                startTime: rest.startTime,
                endTime: rest.endTime,
                id: parseInt(rest.id, 10),
                groupId: parseInt(rest.groupId, 10),
                parentId: rest.parentId !== null ? parseInt(rest.parentId, 10) : null,
                onJob: Boolean(rest.onJob),
            };
        };

        if (Array.isArray(updatedWorkingHour)) {
            return updatedWorkingHour.map(transformWorkingHour);
        } else {
            return transformWorkingHour(updatedWorkingHour);
        }
    };

    const handleChange = (parent, children, toBeUpdated, event, type, selectedTimeZone) => {
        if (type === "onJob") {
            showOrHidRow(parent, selectedTimeZone?.id);
            return;
        }

        const parsedTime = convert24HourTo12Hour(event.target.value, selectedTimeZone.timezone);

        if (parsedTime) {
            const toBeUpdatedWorkingHour = JSON.parse(JSON.stringify(toBeUpdated));
            if (type === "startTime") {
                toBeUpdatedWorkingHour.startTime = parsedTime;
            } else if (type === "endTime") {
                toBeUpdatedWorkingHour.endTime = parsedTime;
            } else {
                console.error("Error:", "Invalid type");
            }

            const updatedWorkingHour = getUpdatedWorkingHour(toBeUpdatedWorkingHour);
            const errors = handleValidate(parent, children, updatedWorkingHour, setTimeRangeErrors, setHasErrors);
            if (errors.length > 0) {
                console.error("Validation failed:", errors);
                return;
            }

            updateWorkingHourData({
                variables: { input: updatedWorkingHour, timeZoneId: selectedTimeZone.id },
            })
                .then((res) => {
                    const updatedWorkingHoursResponse = res.data?.updateWorkingHour;
                    dispatch(updateWorkingHour(selectedBusinessId, updatedWorkingHoursResponse, WorkingHourOperation.UPDATE));
                })
                .catch((error) => {
                    console.error("Error updating working hour:", error);
                });
        } else {
            console.error("Parsed time is invalid:", parsedTime);
        }
    };


    const validateTimeRange = (parent, children, newOrUpdatedRecord) => {
        const errors = [];
        const mergedRecords = [];

        if (parent) {
            mergedRecords.push({ ...parent });
        }
        children.forEach((child) => {
            mergedRecords.push({ ...child });
        });
        if (newOrUpdatedRecord) {
            const existingRecordIndex = mergedRecords.findIndex(
                (record) => parseInt(record.id) === newOrUpdatedRecord.id
            );
            if (existingRecordIndex === -1) {
                mergedRecords.push({ ...newOrUpdatedRecord });
            } else {
                mergedRecords[existingRecordIndex].startTime = newOrUpdatedRecord.startTime;
                mergedRecords[existingRecordIndex].endTime = newOrUpdatedRecord.endTime;
            }
        }

        for (let i = 1; i < mergedRecords.length; i++) {
            const prevRecord = mergedRecords[i - 1];
            const currRecord = mergedRecords[i];

            const prevEnd = convertTimeToMinutes(prevRecord.endTime);
            const currStart = convertTimeToMinutes(currRecord.startTime);
            const currEnd = convertTimeToMinutes(currRecord.endTime);

            if (currStart <= prevEnd && currStart >= currEnd) {
                errors.push({
                    id: currRecord.id,
                    message: `Conflict detected between ID ${prevRecord.id} (endTime: ${prevRecord.endTime}) and ID ${currRecord.id} (startTime: ${currRecord.startTime})`,
                });
            }
        }

        return errors;
    };

    const handleValidate = (parent, children, newRow, setTimeRangeErrors, setHasErrors) => {
        const errors = validateTimeRange(parent, children, newRow);
        if (errors.length > 0) {
            setTimeRangeErrors(errors);
            setHasErrors(true);
        } else {
            setTimeRangeErrors([]);
            setHasErrors(false);
        }
        return errors;
    };



    const handleAddRow = (parent, children, userTimeZone) => {
        const parentId = Number(parent.id);
        let latestEndTime;
        if (children.length === 0) {
            latestEndTime = parent.endTime;
        } else {
            const lastChild = children[children.length - 1];
            latestEndTime = lastChild.endTime;
        }
        if (!latestEndTime || !/am|pm/i.test(latestEndTime)) {
            console.error("Invalid latest end time format:", latestEndTime);
            return;
        }
        latestEndTime = latestEndTime.trim().toLowerCase();
        const endTimeInMinutes = convertTimeToMinutes(latestEndTime);
        if (endTimeInMinutes === null || endTimeInMinutes < 0) {
            console.error("Error converting latestEndTime to minutes:", latestEndTime);
            return;
        }
        const newStartTimeInMinutes = endTimeInMinutes + 60;
        const newEndTimeInMinutes = newStartTimeInMinutes + 60;
        const newStartTime = formatTimeWithPeriod(newStartTimeInMinutes);
        const newEndTime = formatTimeWithPeriod(newEndTimeInMinutes);
        const newRow = createNewChildRow(parentId, parent.groupId, newStartTime, newEndTime);
        const errors = handleValidate(parent, children, newRow, setTimeRangeErrors, setHasErrors);
        if (errors.length > 0) {
            console.error("Error adding working hour:", "Validation failed");
            return;
        }
        addWorkingHourData({ variables: { input: newRow, timeZoneId: userTimeZone.id } })
            .then((res) => {
                let updatedWorkingHoursResponse = res.data?.addWorkingHour;
                if (updatedWorkingHoursResponse && typeof updatedWorkingHoursResponse === "object") {
                    const { __typename, ...rest } = updatedWorkingHoursResponse;
                    updatedWorkingHoursResponse = rest;
                }
                updateWorkingHour(selectedBusinessId, updatedWorkingHoursResponse, WorkingHourOperation.ADD);
            })
            .catch((error) => {
                console.error("Error adding working hour:", error);
            });
    };

    const updateWorkingHour = (
        selectedBusinessId,
        updatedWorkHour, // Now takes an array of WorkingHour
        operation,
        timeZoneId
    ) => {
        if (props.entityType === EntityType.BUSINESS) {
            dispatch(
                updateBusinessWorkingHour(
                selectedBusinessId,
                updatedWorkHour,
                operation,
                timeZoneId
            )
            );
        } else if (props.entityType === EntityType.STAFF) {
            dispatch(
                updateStaffWorkingHour(
                selectedBusinessId,
                props?.providerUser?.staffId,
                    [updatedWorkHour],
                [],
                operation,
                timeZoneId
            )
            );
        }
    };


    const createNewChildRow = (parentId, groupId, startTime, endTime) => ({
        startTime: startTime.trim().toLowerCase().replace(/\s{2,}/g, " "),
        endTime: endTime.trim().toLowerCase().replace(/\s{2,}/g, " "),
        onJob: true,
        parentId: parentId,
        groupId: groupId,
    });

    const handleDeleteRow = (child) => {
        deleteWorkingHourData({
            variables: { workingHourId: child.id },
        })
            .then((res) => {
                const result = res.data.deleteWorkingHour;
                if (result.success) { 
                } else {
                    console.log("Deletion failed:", result.message);
                }
                if (result.success) {
                    updateWorkingHour(selectedBusinessId, child, WorkingHourOperation.DELETE);
                }
            })
            .catch((error) => {
                if (error.response && error.response.errors) {
                    error.response.errors.forEach((graphQLError) => {
                        console.error("GraphQL Error:", graphQLError.message);
                    });
                }
            });
    };

    const handleDeleteWorkingHourGroup = async (groupId) => {
        let confirmation;
        confirmation = await confirm({
            description: "Are you sure you want to delete this working hour group?",
        });

        if (confirmation.confirmed) {
            try {
                await deleteWorkingHourGroupData({ variables: { id: groupId } });
                refetchOverrides && refetchOverrides();
            } catch (err) {
                console.error("Error deleting working hour group:", err);
            }
        }
    };

    const convert24HourTo12Hour = (time24h, userTimeZone) => {
        try {
            const localTime = DateTime.fromFormat(time24h, "HH:mm", {
                zone: userTimeZone.timezone,
            });
            if (!localTime.isValid) {
                throw new Error("Invalid time or timezone format.");
            }
            const time12h = localTime.toFormat("hh:mm a"); 
            return time12h;
        } catch (error) {
            console.error("Error converting time:", error.message);
            return null;
        }
    };

    // New functionality: update the working hours row in place.
    // It accepts an updated working hours array, then calls handleUpdateWorkingHourGroup with the current workingHourGroup.
    const updateWorkingHoursRow = (updatedWorkingHours) => {
        if (workingHourGroup && workingHourGroup.timeZone) {
            handleUpdateWorkingHourGroup(workingHourGroup, updatedWorkingHours, workingHourGroup.timeZone);
        }
    };

    return {
        workingHourGroup,
        hasErrors,
        handleChange,
        handleAddRow,
        handleDeleteRow,
        handleDeleteWorkingHourGroup,
        timeRangeErrors,
        getWorkingHours,
        getWorkingHourHolidays,
        getWorkingHourOverrides,
        handleTimezoneSelect,
        workingHourOverrideGroups,
        refetchOverrides,
        updateWorkingHoursRow, // New function for updating working hours rows
    };
};
